import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiEye, FiCalendar, FiMail, FiTrendingUp } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './LinkSubmissions.css';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useNavigate } from "react-router-dom";

const LinkSubmissions = ({ linkId, onBack, onViewReport }) => {
    const { t, i18n } = useTranslation();
    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortBy, setSortBy] = useState('date-desc');
   
    useEffect(() => {
        fetchLinkStats();
    }, [linkId]);

    const fetchLinkStats = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await fetch(`/api/shared-links/${linkId}/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setLinkData(data);
            } else {
                setError(t('linkSubmissions.dataLoadError'));
            }
        } catch (err) {
            setError(t('linkSubmissions.connectionError'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        return new Date(dateString).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatScore = (score) => {
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        return new Intl.NumberFormat(locale).format(Math.round(score || 0));
    };

    const getScoreColor = (percentage) => {
        if (percentage >= 75) return '#28a745';
        if (percentage >= 50) return '#ffc107';
        return '#dc3545';
    };

    const getStatusText = (status) => {
        switch(status) {
            case 'active':
                return t('linkSubmissions.statusActive');
            case 'expired':
                return t('linkSubmissions.statusExpired');
            default:
                return t('linkSubmissions.statusArchived');
        }
    };

    const sortSubmissions = (submissions) => {
        if (!submissions) return [];
        
        const sorted = [...submissions];
        
        switch (sortBy) {
            case 'date-desc':
                return sorted.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
            case 'date-asc':
                return sorted.sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));
            case 'score-desc':
                return sorted.sort((a, b) => (b.reportId?.totalScore || 0) - (a.reportId?.totalScore || 0));
            case 'score-asc':
                return sorted.sort((a, b) => (a.reportId?.totalScore || 0) - (b.reportId?.totalScore || 0));
            default:
                return sorted;
        }
    };

    if (loading) {
        return (
            <div className="submissions-container">
                <Sidebar />
                <div className="submissions-content">
                    <div className="loading">
                        <div className="loading-spinner"></div>
                        <p>{t('linkSubmissions.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="submissions-container">
                <Sidebar />
                <div className="submissions-content">
                    <div className="error-message">
                        <h3>{error}</h3>
                        <button onClick={onBack}>{t('linkSubmissions.backButton')}</button>
                    </div>
                </div>
            </div>
        );
    }

    const sortedSubmissions = sortSubmissions(linkData?.submissions);

    return (
        <div className="submissions-container">
            <Sidebar />
            <div className="submissions-content">
                <div className="submissions-header">
                    <button className="back-btn" onClick={onBack}>
                        <FiArrowLeft /> {t('linkSubmissions.backButton')}
                    </button>
                    <h1>{linkData?.linkName}</h1>
                    {linkData?.description && (
                        <p className="link-description">{linkData.description}</p>
                    )}
                </div>

                <div className="stats-overview">
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: '#e3f2fd' }}>
                            <FiEye color="#2196F3" size={24} />
                        </div>
                        <div className="stat-info">
                            <h3>{linkData?.viewCount || 0}</h3>
                            <p>{t('linkSubmissions.totalViews')}</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: '#e8f5e9' }}>
                            <FiTrendingUp color="#4CAF50" size={24} />
                        </div>
                        <div className="stat-info">
                            <h3>{linkData?.submissionCount || 0}</h3>
                            <p>{t('linkSubmissions.completedForms')}</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: '#fff3cd' }}>
                            <FiCalendar color="#ffc107" size={24} />
                        </div>
                        <div className="stat-info">
                            <h3>{getStatusText(linkData?.status)}</h3>
                            <p>{t('linkSubmissions.linkStatus')}</p>
                        </div>
                    </div>

                    {linkData?.maxSubmissions && (
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#f3e5f5' }}>
                                <span style={{ fontSize: '24px', color: '#9c27b0' }}>∞</span>
                            </div>
                            <div className="stat-info">
                                <h3>{linkData.maxSubmissions}</h3>
                                <p>{t('linkSubmissions.maxSubmissions')}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="submissions-list-section">
                    <div className="list-header">
                        <h2>
                            {t('linkSubmissions.submissionsTitle')} ({sortedSubmissions.length})
                        </h2>
                        <select 
                            className="sort-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="date-desc">{t('linkSubmissions.sortByDateDesc')}</option>
                            <option value="date-asc">{t('linkSubmissions.sortByDateAsc')}</option>
                            <option value="score-desc">{t('linkSubmissions.sortByScoreDesc')}</option>
                            <option value="score-asc">{t('linkSubmissions.sortByScoreAsc')}</option>
                        </select>
                    </div>

                    {sortedSubmissions.length === 0 ? (
                        <div className="no-submissions">
                            <p>{t('linkSubmissions.noSubmissions')}</p>
                        </div>
                    ) : (
                        <div className="submissions-grid">
                            {sortedSubmissions.map((submission) => (
                                <div key={submission._id} className="submission-card">
                                    <div className="submission-header">
                                        <div className="company-info">
                                            <h3>{submission.entrepreneurName}</h3>
                                            <p className="email-info">
                                                <FiMail size={14} />
                                                {submission.entrepreneurEmail}
                                            </p>
                                        </div>
                                        <div 
                                            className="score-badge"
                                            style={{ 
                                                backgroundColor: getScoreColor(
                                                    submission.reportId?.overallPercentage || 0
                                                )
                                            }}
                                        >
                                            {(submission.reportId?.overallPercentage || 0).toFixed(0)}%
                                        </div>
                                    </div>

                                    <div className="submission-details">
                                        <div className="detail-row">
                                            <span className="label">{t('linkSubmissions.totalScore')}</span>
                                            <span className="value">
                                                {formatScore(submission.reportId?.totalScore || 0)}
                                            </span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="label">{t('linkSubmissions.submissionDate')}</span>
                                            <span className="value">
                                                {formatDate(submission.submittedAt)}
                                            </span>
                                        </div>
                                        {submission.reportId?.entrepreneurInfo?.sector && (
                                            <div className="detail-row">
                                                <span className="label">{t('linkSubmissions.sector')}</span>
                                                <span className="value">
                                                    {submission.reportId.entrepreneurInfo.sector}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <button 
                                        className="view-report-btn"
                                        onClick={() => onViewReport(submission.reportId._id)}
                                    >
                                        <FiEye /> {t('linkSubmissions.viewReport')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="link-meta-info">
                    <p><strong>{t('linkSubmissions.linkCreated')}</strong> {formatDate(linkData?.createdAt)}</p>
                    <p><strong>{t('linkSubmissions.expiresAt')}</strong> {formatDate(linkData?.expiresAt)}</p>
                </div>
            </div>
        </div>
    );
};

export default LinkSubmissions;